import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  Button, CellText, DotsVertical, Download01, EmptyState, Menu, Table,
} from '@opendate-app/setlist';
import ContactsSidebar from './ContactsSidebar';
import ContactFormModal from './ContactFormModal';
import MergeContactsModal from './MergeContactsModal';
import { DebounceInput } from 'react-debounce-input';
import useLocalSearchShortcut from '../../shared/hooks/useLocalSearchShortcut';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import CrmAvatar from '../../shared/components/CrmAvatar';
import CrmSearchInput from '../../shared/components/CrmSearchInput';
import CrmPageLayout from '../../shared/components/CrmPageLayout';
import CSVImporter from '../../CSVImporter/CSVImporter';
import FilterSidebar from '../../shared/components/FilterSidebar';
import ActiveFilterBadges from '../../shared/components/ActiveFilterBadges';
import TableSettings from '../../shared/components/TableSettings';
import SaveViewButton from '../../shared/components/SaveViewButton';
import { CONTACT_FILTER_GROUPS, getActiveFilterCount } from '../constants/contactFiltersConfig';
import { CONTACT_COLUMNS, SORT_OPTIONS, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/contactColumnsConfig';
import { resolveToken } from '../../../shared/marketing/themeTokens';
import crmLabels from '../../shared/utils/crmLabels';

const TABLE_SETTINGS_ICON_STROKE = resolveToken('--sl-text-secondary', '#414651');

const axios = require('axios').default;
const qs = require('qs');

const CONTACT_IMPORTER_KEY = '19c80030-cb08-48e4-a5bf-6c6d73b24bcc';

function ContactsPage({
  csrfToken, team, contactTypes = [], customFieldDefinitions = [], constituentIdsEnabled = false, constituentIdLabel = null, importColumns = [],
}) {
  const labels = crmLabels(team);
  const contactSingularLower = labels.contact.singular.toLowerCase();
  const contactPluralLower = labels.contact.plural.toLowerCase();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(25);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('search') || '');
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [filterValues, setFilterValues] = useState({});
  const [selectedContactIds, setSelectedContactIds] = useState([]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formModalContact, setFormModalContact] = useState(null);
  const importerOpenRef = useRef(null);

  const [contactToRemove, setContactToRemove] = useState(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  // Contacts to merge (null = modal closed). Duplicate review now lives on its
  // own page (/crm/fans/duplicates); this modal is for list multi-select merges.
  const [mergeContacts, setMergeContacts] = useState(null);

  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(getDefaultVisibleColumns());
  const [hiddenColumns, setHiddenColumns] = useState(getDefaultHiddenColumns());
  const [sortBy, setSortBy] = useState('first_name asc');

  const [savedViews, setSavedViews] = useState([]);
  const [currentView, setCurrentView] = useState(null);
  const [hasViewChanges, setHasViewChanges] = useState(false);
  const baselineViewRef = useRef(null);

  const searchInputRef = useRef(null);

  useLocalSearchShortcut(searchInputRef);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilters, sortBy]);

  useEffect(() => {
    setSelectedContactIds([]);
  }, [contacts]);

  useEffect(() => {
    if (!team || !team.id) return;

    const fetchFilterValues = async () => {
      try {
        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }
        const response = await axios.get(`/teams/${team.id}/crm/fans/filter_values.json`);
        setFilterValues(response.data || {});
      } catch (e) {
        toast.error('Failed to load filter options.');
      }
    };

    fetchFilterValues();
  }, [team && team.id, csrfToken]);

  const enrichedFilterGroups = useMemo(() => {
    return CONTACT_FILTER_GROUPS.map(group => ({
      ...group,
      filters: group.filters.filter(filter => filter.id !== 'has_soft_credits' || team.soft_credits_enabled !== false).map(filter => {
        if (filter.id === 'title' && filterValues.titles) {
          return { ...filter, options: filterValues.titles };
        }
        if (filter.id === 'company_name' && filterValues.company_names) {
          return { ...filter, options: filterValues.company_names };
        }
        if (filter.id === 'contact_type') {
          return { ...filter, options: contactTypes.map((type) => ({ value: String(type.id), label: type.name })) };
        }
        if (filter.id === 'city' && filterValues.cities) {
          return { ...filter, options: filterValues.cities };
        }
        if (filter.id === 'state' && filterValues.states) {
          return { ...filter, options: filterValues.states };
        }
        if (filter.id === 'zip' && filterValues.zips) {
          return { ...filter, options: filterValues.zips };
        }
        if (filter.id === 'pledge_status' && filterValues.pledge_statuses) {
          return { ...filter, options: filterValues.pledge_statuses };
        }
        if (filter.id === 'membership_status' && filterValues.membership_statuses) {
          return { ...filter, options: filterValues.membership_statuses };
        }
        if (filter.id === 'has_organizations') {
          return { ...filter, label: `Has ${labels.organization.plural}` };
        }
        return filter;
      })
    }));
  }, [filterValues, contactTypes, team.soft_credits_enabled, labels.organization.plural]);

  const applyExistenceFilter = (q, value, attribute) => {
    if (value.includes('true') && !value.includes('false')) {
      q[`${attribute}_not_null`] = 1;
    } else if (value.includes('false') && !value.includes('true')) {
      q[`${attribute}_null`] = 1;
    }
  };

  const buildRansackParams = () => {
    const q = {};

    if (searchTerm) q.keyword_search = searchTerm;

    Object.entries(activeFilters).forEach(([key, value]) => {
      if (key === 'title' && Array.isArray(value) && value.length > 0) {
        q.title_cont_any = value;
      } else if (key === 'company_name' && Array.isArray(value) && value.length > 0) {
        q.company_name_cont_any = value;
      } else if (key === 'contact_type' && Array.isArray(value) && value.some((id) => /^\d+$/.test(id))) {
        q.contact_types_id_in = value.filter((id) => /^\d+$/.test(id));
      } else if (key === 'has_email' && Array.isArray(value) && value.length > 0) {
        if (value.includes('true') && !value.includes('false')) {
          q.email_present = 1;
        } else if (value.includes('false') && !value.includes('true')) {
          q.email_blank = 1;
        }
      } else if (key === 'has_phone' && Array.isArray(value) && value.length > 0) {
        if (value.includes('true') && !value.includes('false')) {
          q.phone_number_present = 1;
        } else if (value.includes('false') && !value.includes('true')) {
          q.phone_number_blank = 1;
        }
      } else if (key === 'has_street_address' && Array.isArray(value) && value.length > 0) {
        if (value.includes('true') && !value.includes('false')) {
          q.address1_present = 1;
        } else if (value.includes('false') && !value.includes('true')) {
          q.address1_blank = 1;
        }
      } else if (key === 'city' && Array.isArray(value) && value.length > 0) {
        q.city_cont_any = value;
      } else if (key === 'state' && Array.isArray(value) && value.length > 0) {
        q.state_cont_any = value;
      } else if (key === 'zip' && Array.isArray(value) && value.length > 0) {
        q.zip_cont_any = value;
      } else if (key === 'has_transactions' && Array.isArray(value) && value.length > 0) {
        if (value.includes('true') && !value.includes('false')) {
          q.with_paid_orders = true;
        } else if (value.includes('false') && !value.includes('true')) {
          q.without_paid_orders = true;
        }
      } else if (key === 'has_donations' && Array.isArray(value) && value.length > 0) {
        if (value.includes('true') && !value.includes('false')) {
          q.with_paid_donations = true;
        } else if (value.includes('false') && !value.includes('true')) {
          q.without_paid_donations = true;
        }
      } else if (key === 'has_soft_credits' && team.soft_credits_enabled !== false && Array.isArray(value) && value.length > 0) {
        if (value.includes('true') && !value.includes('false')) {
          q.with_paid_soft_credits = true;
        } else if (value.includes('false') && !value.includes('true')) {
          q.without_paid_soft_credits = true;
        }
      } else if (key === 'has_organizations' && Array.isArray(value) && value.length > 0) {
        applyExistenceFilter(q, value, 'organizations_id');
      } else if (key === 'has_households' && Array.isArray(value) && value.length > 0) {
        applyExistenceFilter(q, value, 'households_id');
      } else if (key === 'has_pledges' && Array.isArray(value) && value.length > 0) {
        applyExistenceFilter(q, value, 'pledges_id');
      } else if (key === 'has_memberships' && Array.isArray(value) && value.length > 0) {
        applyExistenceFilter(q, value, 'memberships_id');
      } else if (key === 'pledge_status' && Array.isArray(value) && value.length > 0) {
        q.pledges_status_in = value;
      } else if (key === 'membership_status' && Array.isArray(value) && value.length > 0) {
        q.memberships_status_in = value;
      } else if (key === 'events_count' && typeof value === 'object' && value !== null) {
        if (value.min) q.calendar_events_count_gteq = Number(value.min);
        if (value.max) q.calendar_events_count_lteq = Number(value.max);
      } else if (key === 'created_at' && typeof value === 'object' && value !== null) {
        if (value.start) q.created_at_gteq = value.start;
        if (value.end) q.created_at_lteq = `${value.end}T23:59:59`;
      }
    });

    return q;
  };

  useEffect(() => {
    if (!team || !team.id) return;

    const fetchContacts = async () => {
      try {
        setLoading(true);
        setError(null);
        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }
        const q = buildRansackParams();
        if (sortBy) q.s = sortBy;
        const response = await axios.get(`/teams/${team.id}/crm/fans.json`, {
          params: {
            page: currentPage,
            per_page: perPage,
            q,
          },
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
        });

        const data = response.data || {};
        const contactsFromResponse = data.contacts || [];
        const pagination = data.pagination || {};

        setContacts(contactsFromResponse);
        setTotalEntries(pagination.total_entries || contactsFromResponse.length || 0);
        setTotalPages(pagination.total_pages || 1);
      } catch (e) {
        const fallbackMessage =
          (e.response && typeof e.response.data === 'string' && e.response.data) ||
          `Failed to load ${contactPluralLower}`;
        setError(fallbackMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, [team && team.id, csrfToken, currentPage, perPage, searchTerm, activeFilters, sortBy, reloadToken]);

  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
  };

  const handleApplyFilters = () => {
  };

  const handleRemoveFilter = (filterId) => {
    const newFilters = { ...activeFilters };
    delete newFilters[filterId];
    setActiveFilters(newFilters);
  };

  const handleClearAllFilters = () => {
    setActiveFilters({});
  };

  const toggleSidebar = () => {
    setSidebarOpen((open) => !open);
  };

  const openNewContactModal = () => {
    setFormModalContact(null);
    setFormModalOpen(true);
  };

  // Deep-link support: the CRM dashboard's New/Import buttons land here with
  // ?action=new|import and expect the matching modal to open on arrival.
  // The import path relies on importerOpenRef being set by CSVImporter's
  // renderAnchor, which mounts unconditionally before this effect runs — if the
  // importer ever becomes conditionally/lazily mounted, gate this on its mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    if (action === 'new') openNewContactModal();
    else if (action === 'import') importerOpenRef.current?.();
    else return;

    params.delete('action');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, []);

  const openEditModalForContact = (contact) => {
    setFormModalContact(contact);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setFormModalContact(null);
  };

  const handleContactSaved = (saved, isNew) => {
    if (isNew) {
      setContacts((prev) => [saved, ...prev]);
      setTotalEntries((prev) => (prev || 0) + 1);
    } else {
      setContacts((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
    }
  };

  const exportContacts = async () => {
    try {
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.post('/csv_exports', {
        csv_export: { type: 'crm_contacts', exportable_id: team.id, q: { ...buildRansackParams(), s: sortBy } },
      });
      toast.success("CSV export started, you will receive an email when it's ready to download");
    } catch (e) {
      toast.error('Could not export CSV, something went wrong');
    }
  };

  const handleImportClosed = () => {
    setReloadToken((token) => token + 1);
  };

  const closeRemoveModal = () => {
    setContactToRemove(null);
    setRemoveSubmitting(false);
    setRemoveError(null);
  };

  const handleConfirmRemove = async () => {
    if (!contactToRemove || !team || !team.id) return;

    try {
      setRemoveSubmitting(true);
      setRemoveError(null);
      if (csrfToken) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      }

      await axios.delete(`/teams/${team.id}/crm/fans/${contactToRemove.id}.json`);

      setContacts((prev) => prev.filter((c) => c.id !== contactToRemove.id));
      setTotalEntries((prev) => Math.max((prev || 1) - 1, 0));
      setSelectedContactIds((prev) => prev.filter((id) => id !== contactToRemove.id));
      closeRemoveModal();
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setRemoveError(resp.data.join(', '));
      } else {
        setRemoveError(`Failed to remove ${contactSingularLower}`);
      }
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const selectedContacts = contacts.filter((c) =>
    selectedContactIds.includes(c.id),
  );

  // Merge finished: drop the losers from the list and swap in the merged record.
  const handleMerged = (merged, removedIds) => {
    setContacts((prev) => prev
      .filter((c) => !removedIds.includes(c.id))
      .map((c) => (c.id === merged.id ? merged : c)));
    setTotalEntries((prev) => Math.max((prev || 0) - removedIds.length, 0));
    setSelectedContactIds([]);
    setMergeContacts(null);
  };

  const handleColumnsChange = (newVisible, newHidden) => {
    setVisibleColumns(newVisible);
    setHiddenColumns(newHidden);
    localStorage.setItem(`contactColumns_${team.id}`, JSON.stringify({
      visible: newVisible,
      hidden: newHidden,
    }));
  };

  useEffect(() => {
    if (!team || !team.id) return;
    const saved = localStorage.getItem(`contactColumns_${team.id}`);
    if (saved) {
      try {
        const { visible, hidden } = JSON.parse(saved);
        const knownIds = new Set(CONTACT_COLUMNS.map(c => c.id));
        const validVisible = visible.filter(c => knownIds.has(c.id));
        const validHidden = hidden.filter(c => knownIds.has(c.id));
        const savedIds = new Set([...validVisible.map(c => c.id), ...validHidden.map(c => c.id)]);
        const newColumns = CONTACT_COLUMNS.filter(c => !savedIds.has(c.id) && !c.alwaysVisible);
        setVisibleColumns(validVisible);
        setHiddenColumns([...validHidden, ...newColumns]);
      } catch (e) {
      }
    }
  }, [team && team.id]);

  useEffect(() => {
    if (!team || !team.id) return;
    const stored = localStorage.getItem(`contactViews_${team.id}`);
    if (stored) {
      try {
        setSavedViews(JSON.parse(stored));
      } catch (e) {
      }
    }
  }, [team && team.id]);

  useEffect(() => {
    if (!baselineViewRef.current) {
      setHasViewChanges(false);
      return;
    }
    const baseline = baselineViewRef.current;
    const currentVisibleIds = visibleColumns.map(c => c.id).sort().join(',');
    const baselineVisibleIds = baseline.visibleColumns.sort().join(',');
    const columnsChanged = currentVisibleIds !== baselineVisibleIds;
    const filtersChanged = JSON.stringify(activeFilters) !== JSON.stringify(baseline.activeFilters);
    setHasViewChanges(columnsChanged || filtersChanged);
  }, [visibleColumns, activeFilters]);

  const handleSaveView = (viewName) => {
    const newView = {
      name: viewName,
      visibleFields: visibleColumns.map(c => c.id),
      hiddenFields: hiddenColumns.map(c => c.id),
      activeFilters: { ...activeFilters }
    };
    const updatedViews = [...savedViews, newView];
    setSavedViews(updatedViews);
    localStorage.setItem(`contactViews_${team.id}`, JSON.stringify(updatedViews));
    setCurrentView(viewName);
    baselineViewRef.current = {
      visibleColumns: visibleColumns.map(c => c.id),
      activeFilters: { ...activeFilters }
    };
    setHasViewChanges(false);
  };

  const handleLoadView = (view) => {
    const visibleFromView = CONTACT_COLUMNS.filter(c => view.visibleFields.includes(c.id));
    const hiddenFromView = CONTACT_COLUMNS.filter(c => view.hiddenFields.includes(c.id));
    setVisibleColumns(visibleFromView);
    setHiddenColumns(hiddenFromView);
    setActiveFilters(view.activeFilters || {});
    setCurrentView(view.name);
    baselineViewRef.current = {
      visibleColumns: view.visibleFields,
      activeFilters: view.activeFilters || {}
    };
    setHasViewChanges(false);
    localStorage.setItem(`contactColumns_${team.id}`, JSON.stringify({
      visible: visibleFromView,
      hidden: hiddenFromView,
    }));
  };

  const handleDeleteView = (view) => {
    const updatedViews = savedViews.filter(v => v.name !== view.name);
    setSavedViews(updatedViews);
    localStorage.setItem(`contactViews_${team.id}`, JSON.stringify(updatedViews));
    if (currentView === view.name) {
      setCurrentView(null);
      baselineViewRef.current = null;
      setHasViewChanges(false);
    }
  };

  const formatCreatedAt = (createdAt) => (createdAt
    ? new Date(createdAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(team.time_zone ? { timeZone: team.time_zone } : {}),
    })
    : '—');

  const contactDisplayName = (contact) => [contact.first_name, contact.last_name]
    .filter(Boolean)
    .join(' ');

  const renderContactCell = (contact) => {
    const fullName = contactDisplayName(contact);
    return (
      <div className="team-member-info">
        <CrmAvatar
          firstName={contact.first_name}
          lastName={contact.last_name}
          title={fullName || '(No name)'}
          avatarUrl={contact.avatar_thumb_url}
        />
        <CellText
          primary={(
            <Button as="a" href={`/teams/${team.id}/crm/fans/${contact.id}`} variant="link-color">
              {fullName || '(No name)'}
            </Button>
          )}
          supporting={contact.email || undefined}
        />
      </div>
    );
  };

  const COLUMN_RENDERERS = {
    contact: renderContactCell,
    type: (c) => (c.contact_types || []).map((type) => type.name).join(', ') || '—',
    title: (c) => c.title || '—',
    company: (c) => c.company_name || '—',
    email: (c) => c.email || '—',
    phone: (c) => c.phone_number || '—',
    address1: (c) => c.address1 || '—',
    address2: (c) => c.address2 || '—',
    city: (c) => c.city || '—',
    state: (c) => c.state || '—',
    zip: (c) => c.zip || '—',
    created_at: (c) => formatCreatedAt(c.created_at),
    events: (c) => (typeof c.total_events === 'number' ? c.total_events : '—'),
  };

  const tableColumns = [
    ...visibleColumns
      .filter((col) => COLUMN_RENDERERS[col.id])
      .map((col) => ({
        key: col.id,
        header: col.id === 'contact' ? labels.contact.singular : col.label,
        primary: col.id === 'contact',
        render: COLUMN_RENDERERS[col.id],
      })),
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '48px',
      render: (contact) => (
        <Menu
          placement="bottom-end"
          trigger={(
            <Button
              variant="tertiary"
              size="sm"
              icon={DotsVertical}
              aria-label={`More actions for ${contactDisplayName(contact) || 'contact'}`}
            />
          )}
          items={[
            { label: `Edit ${contactSingularLower}`, onClick: () => openEditModalForContact(contact) },
            { divider: true },
            { label: 'Remove from team', destructive: true, onClick: () => setContactToRemove(contact) },
          ]}
        />
      ),
    },
  ];

  return (
    <CrmPageLayout
      team={team}
      activeSidebarKey="contacts"
      mainTopMargin={32}
      title={labels.contact.plural}
      subtitle={`${totalEntries.toLocaleString()} ${contactPluralLower}`}
      secondaryAction={(
        <>
          <Button variant="secondary" size="lg" icon={Download01} onClick={exportContacts}>Export</Button>
          <CSVImporter
          csrfToken={csrfToken}
          team={team}
          importerKey={CONTACT_IMPORTER_KEY}
          type="CSVImportRows::Contact"
          dynamicColumns={importColumns.length > 0 ? importColumns : undefined}
          onClose={handleImportClosed}
          renderAnchor={(openModal) => {
            importerOpenRef.current = openModal;
            return (
              <button
                type="button"
                className="btn btn-setlist-secondary btn-lg"
                onClick={(e) => {
                  e.preventDefault();
                  openModal();
                }}
              >
                {`Import ${labels.contact.plural}`}
              </button>
            );
          }}
          />
        </>
      )}
      primaryAction={{
        label: `Add ${labels.contact.singular}`,
        iconClass: 'fal fa-plus',
        onClick: openNewContactModal,
      }}
    >
          <div className="card">
            <div className="card-body p-0">
              <div className="border-bottom px-4 py-3">
                <div className="d-flex justify-content-between align-items-end crm-toolbar">
                  <div className="flex-grow-1 me-3 crm-toolbar-search">
                    <CrmSearchInput
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={`Search ${contactPluralLower}...`}
                      ariaLabel="Search contacts"
                      inputRef={searchInputRef}
                      useDebounce
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>

                  <div className="team-search-filters-right d-flex align-items-center gap-2 crm-toolbar-actions">
                    <a
                      href={`/teams/${team.id}/crm/fans/duplicates`}
                      className="btn btn-setlist-secondary"
                    >
                      <span>Review duplicates</span>
                    </a>
                    {selectedContactIds.length >= 2 && (
                      <button
                        type="button"
                        className="btn btn-setlist-secondary"
                        onClick={() => setMergeContacts(selectedContacts)}
                      >
                        <span>{`Merge ${labels.contact.plural}`}</span>
                      </button>
                    )}
                    <SaveViewButton
                      hasChanges={hasViewChanges}
                      savedViews={savedViews}
                      currentView={currentView}
                      onSaveView={handleSaveView}
                      onLoadView={handleLoadView}
                      onDeleteView={handleDeleteView}
                    />
                    <div style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="btn btn-setlist-secondary"
                        onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M5 7.5V2.5M5 17.5V12.5M5 12.5C5.46499 12.5 5.69748 12.5 5.88823 12.4239C6.1305 12.3224 6.32239 12.1305 6.42388 11.8882C6.5 11.6975 6.5 11.465 6.5 11C6.5 10.535 6.5 10.3025 6.42388 10.1118C6.32239 9.8695 6.1305 9.67761 5.88823 9.57612C5.69748 9.5 5.46499 9.5 5 9.5C4.53501 9.5 4.30252 9.5 4.11177 9.57612C3.8695 9.67761 3.67761 9.8695 3.57612 10.1118C3.5 10.3025 3.5 10.535 3.5 11C3.5 11.465 3.5 11.6975 3.57612 11.8882C3.67761 12.1305 3.8695 12.3224 4.11177 12.4239C4.30252 12.5 4.53501 12.5 5 12.5ZM10 17.5V10M10 10C10.465 10 10.6975 10 10.8882 9.92388C11.1305 9.82239 11.3224 9.6305 11.4239 9.38823C11.5 9.19748 11.5 8.96499 11.5 8.5C11.5 8.03501 11.5 7.80252 11.4239 7.61177C11.3224 7.3695 11.1305 7.17761 10.8882 7.07612C10.6975 7 10.465 7 10 7C9.53501 7 9.30252 7 9.11177 7.07612C8.8695 7.17761 8.67761 7.3695 8.57612 7.61177C8.5 7.80252 8.5 8.03501 8.5 8.5C8.5 8.96499 8.5 9.19748 8.57612 9.38823C8.67761 9.6305 8.8695 9.82239 9.11177 9.92388C9.30252 10 9.53501 10 10 10ZM10 7V2.5M15 17.5V15M15 15C15.465 15 15.6975 15 15.8882 14.9239C16.1305 14.8224 16.3224 14.6305 16.4239 14.3882C16.5 14.1975 16.5 13.965 16.5 13.5C16.5 13.035 16.5 12.8025 16.4239 12.6118C16.3224 12.3695 16.1305 12.1776 15.8882 12.0761C15.6975 12 15.465 12 15 12C14.535 12 14.3025 12 14.1118 12.0761C13.8695 12.1776 13.6776 12.3695 13.5761 12.6118C13.5 12.8025 13.5 13.035 13.5 13.5C13.5 13.965 13.5 14.1975 13.5761 14.3882C13.6776 14.6305 13.8695 14.8224 14.1118 14.9239C14.3025 15 14.535 15 15 15ZM15 12V2.5"
                            stroke={TABLE_SETTINGS_ICON_STROKE}
                            strokeWidth="1.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>Table Settings</span>
                      </button>
                      <TableSettings
                        isOpen={tableSettingsOpen}
                        onClose={() => setTableSettingsOpen(false)}
                        visibleFields={visibleColumns}
                        hiddenFields={hiddenColumns}
                        onFieldsChange={handleColumnsChange}
                        sortBy={sortBy}
                        onSortByChange={setSortBy}
                        sortOptions={SORT_OPTIONS}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
                <ActiveFilterBadges
                  activeFilters={activeFilters}
                  filterGroups={enrichedFilterGroups}
                  onRemoveFilter={handleRemoveFilter}
                  onClearAll={handleClearAllFilters}
                  onOpenFilterSidebar={() => setFilterSidebarOpen(true)}
                />

                <span className="text-muted small">
                  {`${totalEntries.toLocaleString()} ${contactPluralLower}`}
                </span>
              </div>

              <FilterSidebar
                isOpen={filterSidebarOpen}
                onClose={() => setFilterSidebarOpen(false)}
                onApply={handleApplyFilters}
                title={`Filter ${labels.contact.plural}`}
                subtitle={`Apply filters to find specific ${contactPluralLower}.`}
                filterGroups={enrichedFilterGroups}
                activeFilters={activeFilters}
                onFilterChange={handleFilterChange}
              />

              <Table
                columns={tableColumns}
                data={contacts}
                getRowKey={(contact) => contact.id}
                selectable
                selectedKeys={selectedContactIds}
                onSelectionChange={setSelectedContactIds}
                loading={loading}
                loadingLabel={`Loading ${contactPluralLower}...`}
                error={error || null}
                empty={<EmptyState size="sm" title={`No ${contactPluralLower} yet.`} />}
                stickyFirstColumn
                stickyLastColumn
                stackBelow={640}
              />

              <TeamPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalEntries}
                pageSize={perPage}
                itemLabel={contactPluralLower}
                onPageChange={(page) => {
                  if (!loading && page >= 1 && page <= totalPages) {
                    setCurrentPage(page);
                  }
                }}
              />
            </div>
          </div>

      <ContactFormModal
        isOpen={formModalOpen}
        onClose={closeFormModal}
        contact={formModalContact}
        team={team}
        csrfToken={csrfToken}
        contactTypes={contactTypes}
        customFieldDefinitions={customFieldDefinitions}
        constituentIdsEnabled={constituentIdsEnabled}
        constituentIdLabel={constituentIdLabel}
        onSaved={handleContactSaved}
      />

      {contactToRemove && (
        <div className="team-modal-overlay">
          <div className="team-modal" style={{ maxWidth: 480 }}>
            <div className="team-modal-header">
              <h3 className="team-modal-title">{`Remove ${contactSingularLower}`}</h3>
              <button
                type="button"
                className="team-modal-close"
                onClick={closeRemoveModal}
                aria-label="Close"
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="team-modal-body">
              {removeError && (
                <div className="alert alert-danger py-2 mb-2">{removeError}</div>
              )}
              <p className="mb-0">Are you sure? This cannot be undone.</p>
            </div>
            <div className="team-modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={closeRemoveModal}
                disabled={removeSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmRemove}
                disabled={removeSubmitting}
              >
                {removeSubmitting ? 'Removing…' : `Delete ${contactSingularLower}`}
              </button>
            </div>
          </div>
        </div>
      )}
{mergeContacts && (
  <MergeContactsModal
    contacts={mergeContacts}
    team={team}
    csrfToken={csrfToken}
    onMerged={handleMerged}
    onClose={() => setMergeContacts(null)}
  />
)}
    </CrmPageLayout>
  );
}

export default ContactsPage;
